import React, { useState, useRef, useEffect, useMemo, Suspense } from "react";
import { Canvas, useFrame, useThree, useLoader } from "@react-three/fiber";
import {
  Sky,
  Loader,
  Html,
  useProgress,
  PerspectiveCamera,
  Environment,
  Lightformer,
  OrbitControls,
  FlyControls,
  Clouds,
  Cloud,
} from "@react-three/drei";
import { Leva } from "leva";
import * as THREE from "three";
import { useControls } from "leva";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { FBXLoader } from "three/examples/jsm/Addons.js";
import { TextureLoader } from "three";
import logo from "../../icons/logoIcon.png";
import { message as messageAntd } from "antd";

const ossUrl = "https://cloud-city.oss-cn-chengdu.aliyuncs.com";

const Light = () => {
  const { lightX, lightY, lightZ, lightIntensity, shadowBias } = useControls("Light", {
    lightX: {
      value: 50,
      min: -100,
      max: 100,
      step: 0.1,
    },
    lightY: {
      value: 100,
      min: -100,
      max: 100,
      step: 0.1,
    },
    lightZ: {
      value: 50,
      min: -100,
      max: 100,
      step: 0.1,
    },
    lightIntensity: {
      value: 10,
      min: 0,
      max: 20,
      step: 1,
    },
    shadowBias: {
      value: -0.001,
      min: -0.01,
      max: 0.01,
      step: 0.0001,
    },
  }, { order: 1 }); // 设置顺序为 1
  return (
    <>
      <directionalLight position={[10, 0, 0]} />
      <directionalLight position={[0, 0, 10]} />
      <directionalLight position={[-10, 0, 0]} />
      <directionalLight position={[0, 0, -10]} />
      <spotLight position={[0, 10, 0]} castShadow intensity={100} />
      <ambientLight intensity={0.8} /> {/* 增加环境光强度 */}
      <directionalLight
        castShadow
        position={[lightX, lightY, lightZ]}
        intensity={lightIntensity} // 增加光源强度
        shadow-mapSize-width={4096} // 增加阴影贴图大小
        shadow-mapSize-height={4096}
        shadow-camera-left={-1000}
        shadow-camera-right={1000}
        shadow-camera-top={1000}
        shadow-camera-bottom={-1000}
        shadow-camera-near={0.5} // 调整阴影相机参数
        shadow-camera-far={1000}
        shadow-bias={shadowBias} // 调整阴影bias来避免阴影痤疮
      />
    </>
  );
};

const Car = ({ freeView }) => {
  const keys = useRef({ w: false, s: false, a: false, d: false, v: false });
  const carRef = useRef();
  const hudGroupRef = useRef();
  const cameraMountRef = useRef();
  const cameraControlsRef = useRef();
  const [speed, setSpeed] = useState(0);

  const car = useLoader(FBXLoader, `${ossUrl}/ID4/Models/ID4.fbx`);
  const bodyWorkBC = useLoader(TextureLoader, `${ossUrl}/ID4/texture/T_ID4_Bodywork_Truck_BC.png`);
  const normalMap = useLoader(TextureLoader, `${ossUrl}/ID4/texture/T_ID4_Bodywork_Truck_N.png`);
  const ormMap = useLoader(TextureLoader, `${ossUrl}/ID4/texture/T_ID4_Bodywork_Truck_ORM.png`);
  const WorkBC1 = useLoader(TextureLoader, `${ossUrl}/ID4/texture/T_ID4_Interior_Truck_01_BC.png`);
  const normalMap1 = useLoader(
    TextureLoader,
    `${ossUrl}/ID4/texture/T_ID4_Interior_Truck_01_N.png`
  );
  const ormMap1 = useLoader(TextureLoader, `${ossUrl}/ID4/texture/T_ID4_Interior_Truck_01_ORM.png`);
  const WorkBC = useLoader(TextureLoader, `${ossUrl}/ID4/texture/T_ID4_Interior_Truck_BC.png`);
  const normalMap2 = useLoader(TextureLoader, `${ossUrl}/ID4/texture/T_ID4_Interior_Truck_N.png`);
  const ormMap2 = useLoader(TextureLoader, `${ossUrl}/ID4/texture/T_ID4_Interior_Truck_ORM.png`);
  useMemo(() => {

    car.traverse((child) => {
      if (child.isMesh) {
        const materials = Array.isArray(child.material) ? child.material : [child.material];
        materials.forEach((material) => {
          if (material.name === "M_Bodywork_Truck") {
            material.map = bodyWorkBC;
            material.normalMap = normalMap;
            material.aoMap = ormMap;
          } else if (material.name === "M_Interior_Truck") {
            material.map = WorkBC;
            material.normalMap = normalMap2;
            material.aoMap = ormMap2;
          } else if (material.name === "M_Interior_Truck_01") {
            material.map = WorkBC1;
            material.normalMap = normalMap1;
            material.aoMap = ormMap1;
          }
        });
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
  }, [car]);

  const { maxSpeed, acceleration, deceleration, turnSpeed, Basemap } = useControls({
    maxSpeed: {
      value: 5,
      min: 0,
      max: 50,
      step: 0.1,
    },
    acceleration: {
      value: 2,
      min: 0,
      max: 10,
      step: 0.1,
    },
    deceleration: {
      value: 3,
      min: 0,
      max: 10,
      step: 0.1,
    },
    turnSpeed: {
      value: 0.2,
      min: 0,
      max: 1,
      step: 0.01,
    },
    Basemap: {
      value: "highway", // ✅ Car 组件默认值
      options: {
        高速公路: "highway",
        城市主干道: "main-city-roads",
        城市支路: "urban-branch-road",
        城市交叉口: "urban-intersection",
        城市隧道: "urban-tunnel",
        深山道路: "mountain-road",
        乡村道路: "rural-road",
        地下停车场: "parking33",
        阅江源: "yuejiangyuan",
      },
    },
  }, { order: 2 }); // 与 ThreeScene 的 Basemap 共享顺序
  // const { camX, camY, camZ, lookX, lookY, lookZ } = useControls("Camera", {
  //   camX: { value: 0.36, min: -5, max: 5, step: 0.01 },
  //   camY: { value: 1.33, min: -5, max: 5, step: 0.01 },
  //   camZ: { value: 0.07, min: -5, max: 5, step: 0.01 },
  //   lookX: { value: -0.13, min: -1, max: 1, step: 0.01 },
  //   lookY: { value: 0.02, min: -1, max: 1, step: 0.01 },
  //   lookZ: { value: 1, min: -1, max: 1, step: 0.01 },
  // });
  const { camX, camY, camZ, lookX, lookY, lookZ } = {
    camX: 0.36,
    camY: 1.33,
    camZ: 0.07,
    lookX: -0.13,
    lookY: 0.02,
    lookZ: 1,
  };
  const logoTexture = useLoader(TextureLoader, logo);
  useEffect(() => {
    const handleKey = (e, isPressed) => {
      const key = e.key.toLowerCase();
      if (["w", "s", "a", "d"].includes(key)) {
        keys.current[key] = isPressed;
      }
    };

    const handleKeyDown = (e) => handleKey(e, true);
    const handleKeyUp = (e) => handleKey(e, false);

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  useEffect(() => {
    switch (Basemap) {
      case "highway":
        carRef.current.position.set(-3.3, -3.5, -234.27); // 高速公路起始位置
        break;
      case "main-city-roads": // 城市主干道起始位置
        carRef.current.position.set(-4.2, -60.0, -197.0);
        break;
      case "urban-branch-road": // 城市支路起始位置
        carRef.current.position.set(-1.0, -6.0, -122.0);
        break;
      case "urban-intersection": // 城市交叉口起始位置
        carRef.current.position.set(-1.7, -4.64, -345.0);
        break;
      case "urban-tunnel": // 城市隧道起始位置
        carRef.current.position.set(-94, -5.2, -250.0);
        break;
      case "mountain-road": // 深山道路起始位置
        carRef.current.position.set(-328, -6.3, -440);
        break;
      case "rural-road": // 乡村道路起始位置
        carRef.current.position.set(-461, -2.3, -630.0);
        carRef.current.scale.set(0.85, 0.85, 0.85); // 缩放
        break;
      case "parking33": // 地下停车场起始位置
        carRef.current.position.set(0, -3.5, -1.5);
        break;
      case "yuejiangyuan": // 阅江源起始位置
        carRef.current.position.set(112, -25, -100.0);
        break;
      default:
        carRef.current.position.set(0, 0, 0); // 默认位置
        carRef.current.scale.set(1, 1, 1); // 默认缩放
        break;
    }
  }, [Basemap]);

  useFrame((_, delta) => {
    if (
      !carRef.current ||
      !cameraMountRef.current ||
      !cameraControlsRef.current ||
      freeView // 如果是自由视角模式，不更新相机
    )
      return;

    if (keys.current.w) setSpeed((prev) => Math.min(prev + acceleration * delta, maxSpeed));
    else if (keys.current.s) setSpeed((prev) => Math.max(prev - acceleration * delta, -maxSpeed));
    else setSpeed((prev) => THREE.MathUtils.lerp(prev, 0, deceleration * delta));

    let turn = 0;
    if (keys.current.a) turn += turnSpeed * delta;
    if (keys.current.d) turn -= turnSpeed * delta;
    carRef.current.rotation.y += turn * Math.sign(speed);

    const direction = new THREE.Vector3(0, 0, 1)
      .applyQuaternion(carRef.current.quaternion)
      .multiplyScalar(speed * delta);
    carRef.current.position.add(direction);

    const _cameraPosition = cameraMountRef.current.getWorldPosition(new THREE.Vector3());
    cameraControlsRef.current.position.copy(_cameraPosition);

    const carForward = new THREE.Vector3(lookX, lookY, lookZ)
      .applyQuaternion(carRef.current.quaternion)
      .normalize();

    const target = _cameraPosition.clone().add(carForward.multiplyScalar(5));

    cameraControlsRef.current.lookAt(target);
  });
  return (
    <>
      <group ref={carRef}>
        <primitive
          object={car}
          scale={0.01}
          position={[0, 0, 0]}
          rotation={[-Math.PI / 2, 0, -Math.PI / 2]}
        />
        <mesh position={[0.8, 1.2, 0.45]} rotation={[Math.PI, 0, Math.PI]} scale={[0.4, 0.4, 0.4]}>
          <planeGeometry args={[0.2, 0.1]} />
          <meshBasicMaterial map={logoTexture} transparent />
        </mesh>
        <mesh
          position={[0.2, 1.355, 0.25]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={[0.2, 0.2, 0.2]}
        >
          <planeGeometry args={[0.2, 0.1]} />
          <meshBasicMaterial map={logoTexture} transparent />
        </mesh>
        <axesHelper />
        <group ref={cameraMountRef} position={[camX, camY, camZ]} />
      </group>
      {!freeView && <PerspectiveCamera ref={cameraControlsRef} makeDefault fov={75} />}
    </>
  );
};

const CloudSystem = () => {
  return (
    <Clouds material={THREE.MeshLambertMaterial}>
      <Cloud
        concentrate="outside"
        growth={100}
        color="#fff"
        opacity={0.5}
        seed={1}
        bounds={[1000, 2500, 100]}
        volume={500}
        rotation={[-90 * (Math.PI / 180), 0, -90 * (Math.PI / 180)]}
        position={[0, 300, 0]}
      />
      <Cloud
        concentrate="outside"
        growth={100}
        color="#fff"
        opacity={0.5}
        seed={1}
        bounds={[1000, 2500, 100]}
        volume={500}
        rotation={[-90 * (Math.PI / 180), 0, -90 * (Math.PI / 180)]}
        position={[1000, 500, 2000]}
      />
      <Cloud
        concentrate="outside"
        growth={100}
        color="#fff"
        opacity={0.5}
        seed={1}
        bounds={[1000, 2500, 100]}
        volume={500}
        rotation={[-90 * (Math.PI / 180), 0, -90 * (Math.PI / 180)]}
        position={[1000, 400, -2000]}
      />
      <Cloud
        concentrate="outside"
        growth={100}
        color="#fff"
        opacity={0.5}
        seed={1}
        bounds={[1000, 2500, 100]}
        volume={500}
        rotation={[-90 * (Math.PI / 180), 0, -90 * (Math.PI / 180)]}
        position={[-2000, 400, -2000]}
      />
      <Cloud
        concentrate="outside"
        growth={100}
        color="#fff"
        opacity={0.5}
        seed={1}
        bounds={[1000, 2500, 100]}
        volume={500}
        rotation={[-90 * (Math.PI / 180), 0, -90 * (Math.PI / 180)]}
        position={[500, 500, 0]}
      />
    </Clouds>
  );
};

const City = ({environmentVal}) => {
  let environment = environmentVal || 'highway'
  const [modelScene, setModelScene] = useState(null);
  let modelPath = "";
  if (environment === "yuejiangyuan") {
    modelPath = `${ossUrl}/maps/${environment}.glb`;
  } else {
    modelPath = ["urban-tunnel", "mountain-road", "rural-road", "parking33"].includes(environment)
      ? `${ossUrl}/maps/${environment}/${environment}.gltf`
      : `${ossUrl}/maps/${environment}.gltf`;
  }
  const city = useLoader(GLTFLoader, modelPath);

  useEffect(() => {
    if (city && city.scene) {
      // 克隆模型，避免污染原始缓存模型
      const clonedScene = city.scene.clone(true);

      // 修正模型中心点
      const box = new THREE.Box3().setFromObject(clonedScene);
      const center = box.getCenter(new THREE.Vector3());
      clonedScene.position.sub(center);

      switch (environment) {
        case "urban-tunnel":
          clonedScene.rotation.set(0, 16 * (Math.PI / 180), 0);
          break;
        case "mountain-road":
          clonedScene.rotation.set(0, 128 * (Math.PI / 180), 0);
          break;
        case "rural-road":
          clonedScene.rotation.set(0, 139 * (Math.PI / 180), 0);
          break;
        case "yuejiangyuan":
          clonedScene.position.set(0, -25, 0);
          clonedScene.rotation.set(0, 40 * (Math.PI / 180), 0);
          break;
        default:
          clonedScene.rotation.set(0, 0, 0);
          break;
      }

      // 设置阴影
      clonedScene.traverse((child) => {
        if (child.isMesh) {
          child.receiveShadow = true;
          child.castShadow = true;
        }
      });

      setModelScene(clonedScene);
    }

    return () => {
      setModelScene(null);
    };
  }, [city]);

  if (!modelScene) {
    return null;
  }

  return (
    <>
      <primitive object={modelScene} />
      {/* {
        environment === "yuejiangyuan" && <CloudSystem />
      } */}
      {/* <ambientLight intensity={0.5} />
      <directionalLight position={[0, 10, 10]} intensity={0.5} castShadow /> */}
    </>
  );
};

const LoadingScreen = () => {
  const { progress, errors } = useProgress();

  if (errors.length > 0) {
    return (
      <Html center>
        <div
          style={{
            background: "rgba(0,0,0,0.8)",
            padding: "20px",
            borderRadius: "10px",
            color: "white",
            fontFamily: "Arial",
            textAlign: "center",
          }}
        >
          <div style={{ color: "#ff4444" }}>Error loading model</div>
          <div style={{ marginTop: "10px", fontSize: "0.8em" }}>
            {errors.map((error, index) => (
              <div key={index}>{error}</div>
            ))}
          </div>
        </div>
      </Html>
    );
  }

  return (
    <Html center>
      <div
        style={{
          background: "rgba(0,0,0,0.8)",
          padding: "20px",
          borderRadius: "10px",
          color: "white",
          fontFamily: "Arial",
          textAlign: "center",
        }}
      >
        <div>Loading Environment...</div>
        <div
          style={{
            width: "200px",
            height: "10px",
            background: "#333",
            borderRadius: "5px",
            marginTop: "10px",
          }}
        >
          <div
            style={{
              width: `${progress}%`,
              height: "100%",
              background: "#4CAF50",
              borderRadius: "5px",
              transition: "width 0.3s ease-in-out",
            }}
          />
        </div>
        <div style={{ marginTop: "10px" }}>{progress.toFixed(0)}%</div>
      </div>
    </Html>
  );
};

const Init = () => {
  const scene = useThree((state) => state.scene);
  const Ambient = new THREE.AmbientLight(0xffffff, 0.5);
  scene.add(Ambient);

  const Sun = new THREE.DirectionalLight(0xffffff, 0.5);
  Sun.position.set(0, 1, 1);
  Sun.castShadow = true;

  scene.add(Sun);
};

// Canvas 加载监听组件
const CanvasLoadListener = ({ onReady }) => {
  const { progress } = useProgress();
  const [hasNotified, setHasNotified] = React.useState(false);


  React.useEffect(() => {
    if (progress === 100 && !hasNotified) {
      setHasNotified(true);
      // 延迟一小段时间确保渲染完成
      setTimeout(() => {
        onReady();
      }, 500);
    }
  }, [progress, hasNotified, onReady]);

  return null;
};

const ThreeScene = () => {
  const [hudObjects, setHudObjects] = useState([]);
  const [style, setStyle] = useState({});
  const [images, setImages] = useState([]);
  const [texts, setTexts] = useState([]);
  const [rect, setRect] = useState({});
  const [proportion, setProportion] = useState(0);
  const [bg, setBg] = useState({});
  const [objectsNow, setObjectsNow] = useState([]);
  const [bgImg, setBgImage] = useState({});
  const [freeView, setFreeView] = useState(false);
  const [canvasReady, setCanvasReady] = useState(false); // 追踪 Canvas 是否准备好
  const [pendingHudData, setPendingHudData] = useState(null); // 暂存待设置的 HUD 数据
  const [currentIndex, setCurrentIndex] = useState(0); // 始终从第一个画布开始
  const [levaKey, setLevaKey] = useState(0); // 用于强制重新挂载 Leva 组件

  // // 组件挂载时清除特定的 leva 缓存
  // useEffect(() => {
  //   // 只清除 HUD 控制相关的 leva 缓存
  //   const levaStorageKey = 'leva__HUD 控制.画布';
  //   localStorage.removeItem(levaStorageKey);
  //   localStorage.removeItem("currentIndex");
    
  //   // 强制重新挂载 Leva
  //   setLevaKey(prev => prev + 1);
  // }, []);

  const { Basemap } = useControls({
    Basemap: {
      options: {
        高速公路: "highway",
        城市主干道: "main-city-roads",
        城市支路: "urban-branch-road",
        城市交叉口: "urban-intersection",
        城市隧道: "urban-tunnel",
        深山道路: "mountain-road",
        乡村道路: "rural-road",
        地下停车场: "parking33",
        阅江源: "yuejiangyuan",
      },
    },
  }, { order: 2 }); // 设置顺序为 2，显示在 Light 下方

  const { movementSpeed, rollSpeed } = useControls(
    "自由视角控制",
    {
      movementSpeed: {
        value: 50,
        min: 1,
        max: 200,
        step: 1,
        label: "移动速度",
      },
      rollSpeed: {
        value: 1,
        min: 0.1,
        max: 5,
        step: 0.1,
        label: "旋转速度",
      },
    },
    { collapsed: true, order: 3 } // 设置顺序为 3，显示在 Basemap 下方
  );

  useEffect(() => {
    if (hudObjects.length > 0) {
      // 如果当前索引超出范围，重置为 0
      if (currentIndex >= hudObjects.length) {
        setCurrentIndex(0);
        return;
      }
      if (hudObjects[currentIndex]) {
        setObjectsNow(hudObjects[currentIndex]);
      }
    }
  }, [hudObjects, currentIndex]);

  // 切换画布（仅在当前会话中生效，退出后重置）
  const switchIndex = (index) => {
    console.log(index, '切换画布');
    setCurrentIndex(index);
  }

  // 动态生成下拉选项 - 只在有数据时渲染
  useControls(
    "HUD 控制",
    () => {
      if (hudObjects.length === 0) {
        return {}; // 没有数据时返回空对象
      }
      
      const options = hudObjects.reduce((acc, group, index) => {
        const name = group[0]?.name || `HUD ${index}`;
        acc[name] = index;
        return acc;
      }, {});
      
      return {
        "画布": {
          value: currentIndex,
          options: options,
          onChange: (value) => switchIndex(value),
          transient: false,
        },
      };
    },
    { order: 0 }, 
    [hudObjects, currentIndex]
  );

  // 加载项目数据
  useEffect(() => {
    const projectList = JSON.parse(localStorage.getItem("projectList"));
    if (!projectList || projectList.length === 0) {
      console.error("projectList 为空或不存在");
      return messageAntd.error("projectList不存在请返回编辑页面重试");
    }
    
    // 处理 data 字段，可能是字符串也可能是对象
    let projectData = projectList[0].data;
    if (typeof projectData === "string") {
      projectData = JSON.parse(projectData);
    }
    
    if (!projectData || !projectData.objects) {
      console.error("项目数据格式不正确", projectData);
      return;
    }
    
    const message = projectData.objects;
    const bgImage = message.find((item) => item.name === "bgImage");
    setBgImage(bgImage);
    
    const groupedObjects = message.reduce((acc, obj) => {
      if (obj.type === "rect") {
        acc.push([obj]);
      } else {
        const parentGroup = acc.find((group) => group[0].id === obj.root);
        if (parentGroup) {
          parentGroup.push(obj);
        }
      }
      return acc;
    }, []);
    
    // 如果 Canvas 已经准备好，立即设置；否则暂存数据
    if (canvasReady) {
      setHudObjects(groupedObjects);
    } else {
      setPendingHudData(groupedObjects);
    }
  }, [canvasReady]);
  
  // 当 Canvas 准备好且有待设置的数据时，设置 HUD 对象
  useEffect(() => {
    if (canvasReady && pendingHudData) {
      // console.log("=== Canvas 加载完成，设置 HUD 对象 ===");
      setHudObjects(pendingHudData);
      setPendingHudData(null); // 清空暂存数据
      // 确保索引重置为 0
      setCurrentIndex(0);
    }
  }, [canvasReady, pendingHudData]);

  useEffect(() => {
    if (objectsNow.length > 0) {
      const imgs = objectsNow.filter((item) => item.name === "image");
      const texts = objectsNow.filter((item) => item.name === "text");
      const rect = objectsNow.filter((item) => item.type === "rect");
      
      setStyle({
        width: rect[0].width * rect[0].scaleX * 0.4,
        height: rect[0].height * rect[0].scaleY * 0.4,
      });
      setRect({
        left: (rect[0].left / bgImg.width) * 100,
        top: (rect[0].top / bgImg.height) * 100,
        width: rect[0].width * rect[0].scaleX,
        height: rect[0].height * rect[0].scaleY,
      });
      setBg({
        width: bgImg.width,
        height: bgImg.height,
      });
      setProportion(rect[0].scaleX * 0.67);
      setImages(imgs);
      setTexts(texts);
    }
  }, [objectsNow]);

  useEffect(() => {
    console.log(rect, 'rect当前的对象');
  }, [rect]);

  const [iconIndices, setIconIndices] = useState({});
  useEffect(() => {
    // 初始化每个图的 icon index 为 0
    const initialIndices = {};
    images.forEach((_, i) => {
      initialIndices[i] = 0;
    });
    setIconIndices(initialIndices);

    const timer = setInterval(() => {
      setIconIndices((prev) => {
        const updated = { ...prev };
        images.forEach((image, i) => {
          if (image.duplicateIcons?.length) {
            updated[i] = (prev[i] + 1) % image.duplicateIcons.length;
          }
        });
        return updated;
      });
    }, 3000);

    return () => clearInterval(timer);
  }, [images]);

  // 计算相对于 rect 的位置百分比
  const getLeftPercent = (item, rect, bg) => {
    return ((item.left - rect.left * bg.width / 100) / rect.width) * 100;
  };
  const getTopPercent = (item, rect, bg) => {
    return ((item.top - rect.top * bg.height / 100) / rect.height) * 100;
  };

  return (
    <div style={{ width: "100vw", height: "100vh", overflow: "hidden", position: "relative" }}>
      <Leva key={levaKey} collapsed={true} hideCopyButton={true} />
      <>
        {objectsNow.length > 0 && !freeView && (
          <div
            style={{
              width: "100vw",
              height: "100vh",
              position: "fixed",
              top: "80px",
              left: 0,
              zIndex: 10,
            }}
          >
            <div
              style={{
                height: `${(rect.height / bg.height) * 100}%`,
                width: `${(rect.width / bg.width) * 100}%`,
                fontFamily: "Arial",
                position: "relative",
                left: `${rect.left}%`,
                top: `${rect.top}%`,
              }}
            >
              {images.map((item, index) => {
                const currentIcon = item.duplicateIcons?.[iconIndices[index]] || item;
                return (
                  <img
                    key={index}
                    src={currentIcon.url}
                    alt="HUD"
                    style={{
                      position: "absolute",
                      left: `${(item.percentageX !== undefined ? item.percentageX : getLeftPercent(item, rect, bg) / 100) * 100}%`,
                      top: `${(item.percentageY !== undefined ? item.percentageY : getTopPercent(item, rect, bg) / 100) * 100}%`,
                      width: `${item.width * item.scaleX * proportion}px`,
                      height: `${item.height * item.scaleY * proportion}px`,
                      opacity: item.opacity || 1, // 添加透明度控制
                    }}
                  />
                );
              })}
              {texts.map((item, index) => {
                return (
                  <div
                    key={index}
                    style={{
                      position: "absolute",
                      left: `${(item.percentageX !== undefined ? item.percentageX : getLeftPercent(item, rect, bg) / 100) * 100}%`,
                      top: `${(item.percentageY !== undefined ? item.percentageY : getTopPercent(item, rect, bg) / 100) * 100}%`,
                      fontSize: `${item.height * item.scaleY * proportion * 0.99}px`,
                      color: item.fill,
                    }}
                  >
                    {item.text}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            background: "rgba(0, 0, 0, 0.7)",
            color: "white",
            padding: "10px 20px",
            borderRadius: "5px",
            zIndex: 1000,
            fontFamily: "Arial",
            fontSize: "14px",
            userSelect: "none",
          }}
        >
          此网页为在线演示，专业版可离线部署
        </div>
        <button
          onClick={() => setFreeView(!freeView)}
          style={{
            position: "fixed",
            bottom: "20px",
            left: "20px",
            background: freeView ? "rgba(76, 175, 80, 0.8)" : "rgba(0, 0, 0, 0.7)",
            color: "white",
            padding: "10px 20px",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
            zIndex: 1000,
            fontFamily: "Arial",
            fontSize: "14px",
            transition: "background-color 0.3s",
          }}
        >
          {freeView ? "返回驾驶视角" : "自由视角"}
        </button>
        <Canvas shadows gl={{ antialias: true }}>
          <Suspense fallback={null}>
            <CanvasLoadListener onReady={() => setCanvasReady(true)} />
            {freeView ? (
              <>
                <PerspectiveCamera makeDefault position={[0, 5, 10]} fov={75} />
                <FlyControls
                  movementSpeed={movementSpeed}
                  rollSpeed={rollSpeed}
                  dragToLook={true}
                  makeDefault
                />
              </>
            ) : null}
            <Car freeView={freeView} />
            <Light />
            <City environmentVal={Basemap} />
            {Basemap === "yuejiangyuan" && (
              <Environment
                background
                files="https://cloud-city.oss-cn-chengdu.aliyuncs.com/horn-koppe_spring_4k.exr"
              />
            )}

            {Basemap !== "yuejiangyuan" && (
              <>
                <Sky distance={450000} sunPosition={[0, 10, 0]} inclination={0} azimuth={0.25} />
                <Environment background={false} environmentIntensity={0.3}>
                  <Lightformer
                    form="rect"
                    intensity={1}
                    color="white"
                    scale={[10, 5]}
                    target={[0, 0, 0]}
                  />
                </Environment>
              </>
            )}
          </Suspense>
        </Canvas>
        <Loader />
      </>
    </div>
  );
};

export default ThreeScene;
